import {
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Catalog } from 'src/app/common/enumerations/enumerations';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { User, UserCatalog } from 'src/app/components/catalogs/user-catalog/services/user';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { utils } from 'src/app/modules/libs/utils';
import { PendingCatalog } from 'src/app/services/pending-approvals/pending-approval';
import { UserRole } from 'src/app/services/user-roles/user-role';
import { UserRoleNote } from 'src/app/services/user-roles/user-role-note';
import { UserRoleService } from 'src/app/services/user-roles/user-role.service';
import { CatalogService } from '../../catalog-service';
import { AddUserRoleNoteComponent } from '../add-user-role-note-component/add-user-role-note.component';
import { RoleUserComponent } from '../role-user/role-user.component';

@Component({
  selector: 'app-role-users',
  templateUrl: './role-users.component.html',
  styleUrls: ['./role-users.component.scss'],
})
export class RoleUsersComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  @Input() role?: Role | null;
  @Input() displayedRole?: Role | null;
  @Input() modifying = false;
  @Input() adding = false;
  @Input() dragEnabled = false;
  @Input() isOnlyPTC? = false;
  @Input() showRoleModified = false;

  @Output() usersChange = new EventEmitter<UserRole[]>();
  @Output() dirty = new EventEmitter<void>();

  @ViewChild('usersListTable') usersListTable!: MatTable<any>;
  @ViewChild('usersList') usersList!: CdkDropList;

  displayedColumnsRoleUsers: string[] = ['name', 'notes'];
  dataSourceRoleUsers: MatTableDataSource<UserRole> = new MatTableDataSource();

  errorMsg: string[] = [];
  valid!: boolean;
  userCatalogList: UserCatalog[] = [];

  constructor(
    protected override injector: Injector,
    private _catalogService: CatalogService,
    private _userRoleService: UserRoleService,
    private router: Router,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadUserCatalogs();
    this.setColumns();
    this.setDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['role'] || changes['displayedRole']) {
      this.setDataSource();
    }

    if (
      changes['modifying'] ||
      changes['adding'] ||
      changes['isOnlyPTC']
    ) {
      this.setColumns();
    }
  }

  private setColumns(): void {
    this.displayedColumnsRoleUsers = ['name', 'notes'];

    if (this.isOnlyPTC) return;

    if (this.modifying) {
      this.displayedColumnsRoleUsers.push('noteActions');
      this.displayedColumnsRoleUsers.push('add');
      return;
    }

    if (this.adding) {
      this.displayedColumnsRoleUsers.push('add');
    }
  }

  private setDataSource(): void {
    const roleUsers = this.getUserRolesOrderedWithOrderField([
      ...((this.role?.userRole ?? []) as UserRole[]),
    ]);
    const displayedUsers = this.getUserRolesOrderedWithOrderField([
      ...((this.displayedRole?.userRole ?? []) as UserRole[]),
    ]);
    const pendingUsers = this.getPendingRoleUsers();

    if (this.role) this.role.userRole = roleUsers;
    if (this.displayedRole) this.displayedRole.userRole = displayedUsers;

    this.dataSourceRoleUsers = new MatTableDataSource(
      this.getUsersForDisplay(roleUsers, displayedUsers, pendingUsers),
    );
    this.emitUsers();
  }

  get usersCount(): number {
    return this.dataSourceRoleUsers.data.length;
  }

  getRoleUsersForSave(): UserRole[] {
    this.setUserRolesOrder(false);
    this.syncDisplayedRoleUsers();
    return (this.dataSourceRoleUsers.data ?? [])
      .filter((userRole) => !this.isPendingDelete(userRole))
      .map((userRole) => {
        const userRoleForSave = { ...userRole };
        delete userRoleForSave.pendingDelete;
        return userRoleForSave;
      });
  }

  clearUsers(): void {
    this.dataSourceRoleUsers = new MatTableDataSource<UserRole>([]);
    this.syncDisplayedRoleUsers();
    this.emitUsers();
  }

  drop(event: any) {
    const users = this.dataSourceRoleUsers.data;
    if (event.previousContainer === event.container) {
      moveItemInArray(
        users,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        users,
        event.previousIndex,
        event.currentIndex,
      );
    }
    this.dataSourceRoleUsers.data = [...users];
    this.usersListTable?.renderRows();
    this.reorderRoleUserData();
    this.markDirty();
  }

  setUserRolesOrder(compact = true) {
    const users = (this.dataSourceRoleUsers.data as UserRole[]).filter(
      (userRole) => !userRole.pendingDelete,
    );
    if (!compact) {
      users.forEach((user, index) => {
        user.order = user.order ?? index + 1;
      });
      return;
    }

    users.map((user) => (user.order = 0));
    users.map((user) => {
      const maximumOrder = Math.max.apply(
        Math,
        users?.map((o) => o.order ?? 0),
      );
      user.order = maximumOrder + 1;
      return user;
    });
  }

  reorderRoleUserData() {
    this.setUserRolesOrder();
  }

  addUserRole(user: User) {
    const userRole: UserRole = {
      roleID: this.displayedRole?.id,
      userID: user.id,
      user,
    };
    this.dataSourceRoleUsers.data.push(userRole);
    this.dataSourceRoleUsers.filter = '';
    this.reorderRoleUserData();
    this.markDirty();
  }

  openAddRoleUser() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = new User();
    const dialogRef = this.dialog.open(RoleUserComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (this.isUserRoleRepeated(this.dataSourceRoleUsers.data, data)) {
          this.addUserRole(data);
        } else {
          this.alertError();
        }
        this.errorMsg = [];
      }
    });
  }

  isUserRoleRepeated(roleUsers: UserRole[], user: User): boolean {
    this.valid = true;
    if (roleUsers.filter((x) => x.userID == user.id).length != 0) {
      this.errorMsg.push('User <b>' + user.name + '</b> is already added');
      this.valid = false;
    }
    return this.valid;
  }

  alertError() {
    this.alert.error(this.errorMsg.join(', '));
  }

  deleteRoleUser(userRole: UserRole) {
    if (this.isPendingDelete(userRole)) {
      this.alert.error(`${userRole.user?.name ?? 'This user'} is already pending to be deleted from the role.`);
      return;
    }

    if (this.userHasPendingChange(userRole)) {
      this.alert.error(`${userRole.user?.name ?? 'This user'} has a pending change in the User Catalog and cannot be removed at this time.`);
      return;
    }

    if (userRole.userID) {
      this._userRoleService.Read(userRole.userID).subscribe(allUserRoles => {
        const otherRoles = allUserRoles.filter(ur => ur.roleID !== this.displayedRole?.id);
        if (otherRoles.length === 0) {
          const userName = userRole.user?.name ?? 'this user';
          const dialogConfig = new MatDialogConfig();
          dialogConfig.width = '500px';
          dialogConfig.data = {
            message: `You are removing <b>${userName}</b> from their final role, which will set them to Inactive upon approval. Are you sure you want to do this?`,
            icon: 'warn',
          };
          this.dialog.open(YesNoDialogComponent, dialogConfig).afterClosed().subscribe(confirmed => {
            if (confirmed) {
              this.removeUserFromRole(userRole);
            }
          });
        } else {
          this.removeUserFromRole(userRole);
        }
      });
    } else {
      this.removeUserFromRole(userRole);
    }
  }

  private removeUserFromRole(userRole: UserRole) {
    if (this.isExistingRoleUser(userRole)) {
      userRole.pendingDelete = true;
    } else {
      const index = this.dataSourceRoleUsers.data.indexOf(userRole);
      this.dataSourceRoleUsers.data.splice(index, 1);
    }
    this.dataSourceRoleUsers.filter = '';
    this.markDirty();
  }

  undeleteRoleUser(userRole: UserRole) {
    userRole.pendingDelete = false;
    this.dataSourceRoleUsers.filter = '';
    this.markDirty();
  }

  openNoteEditor(
    userRole: UserRole,
    note: UserRoleNote | undefined = undefined,
  ) {
    if (this.isPendingDelete(userRole)) {
      this.alert.error(`${userRole.user?.name ?? 'This user'} is pending to be deleted from the role and cannot be modified.`);
      return;
    }

    let index = -1;
    if (note) {
      const notes = this.dataSourceRoleUsers.data.find(
        (user) => user.userID === userRole.userID,
      )?.notes;
      index = notes?.indexOf(note, 0) ?? -1;
    }
    const dialogRef = this.dialog.open(AddUserRoleNoteComponent, {
      width: '400px',
      data: note,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (note && index >= 0) {
          this.updateNote(result, userRole, index);
        } else {
          this.createNoteInCatalog(result, userRole);
        }
        this.markDirty();
      }
    });
  }

  createNoteInCatalog(note: UserRoleNote, userRole: UserRole) {
    note.userRoleID = userRole.roleID;
    const users = this.dataSourceRoleUsers.data as UserRole[];
    let notes =
      users.find((user) => user.userID == userRole.userID)?.notes ?? [];
    notes = notes == undefined ? [] : notes;
    notes.push(note);
    const user = users.find((user) => user.userID == userRole.userID);
    if (user) user.notes = notes;
    this.alert.message('add', [new MessagePlaceholder('{what}', 'Note')]);
  }

  updateNote(note: UserRoleNote, userRole: UserRole, index: number) {
    note.userRoleID = userRole.roleID;
    const noteData = this.dataSourceRoleUsers.data.find(
      (user) => user.userID === userRole.userID,
    )?.notes?.[index];
    if (noteData) noteData.description = note.description;
    this.alert.message('add', [new MessagePlaceholder('{what}', 'Note')]);
  }

  deleteNote(userRole: UserRole, note: UserRoleNote) {
    if (this.isPendingDelete(userRole)) {
      this.alert.error(`${userRole.user?.name ?? 'This user'} is pending to be deleted from the role and cannot be modified.`);
      return;
    }

    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      minWidth: '400px',
      data: {
        message: this.getMessage('Confirm_Note_Delete_Question')?.description,
        icon: 'stop',
      },
    });
    dialogRef.afterClosed().subscribe((dataReturned) => {
      if (dataReturned) {
        const users = this.dataSourceRoleUsers.data as UserRole[];
        const notes = users.find(
          (user) => user.userID == userRole.userID,
        )?.notes;
        const index = notes?.indexOf(note, 0) ?? -1;
        if (index >= 0) {
          notes?.splice(index, 1);
          this.markDirty();
        }
      }
    });
  }

  loadUserCatalogs(): void {
    if (this._catalogService.userCatalogs?.length) {
      this.userCatalogList = this._catalogService.userCatalogs;
      return;
    }
    this._catalogService.GetJoinCreated(Catalog.Users, false).subscribe(data => {
      this.userCatalogList = data.userCatalogs ?? [];
      this._catalogService.userCatalogs = this.userCatalogList;
    });
  }

  userHasPendingChange(ur: UserRole): boolean {
    return !!this.userCatalogList.find(uc => uc.details.user.id === ur.userID)?.pendingUser?.id;
  }

  getUserRolesOrderedWithOrderField(userRoles: UserRole[]): UserRole[] {
    if (userRoles?.some((user) => user.order != 0)) {
      userRoles = userRoles.sort((a, b) => utils.sort(a.order, b.order));
    } else {
      userRoles = userRoles?.slice().sort((a, b) => {
        const lastNameA =
          a.user?.name?.trim().split(/\s+/).pop()?.toLowerCase() || '';
        const lastNameB =
          b.user?.name?.trim().split(/\s+/).pop()?.toLowerCase() || '';
        return lastNameA.localeCompare(lastNameB);
      });
      userRoles = userRoles?.map((userRole, index) => {
        userRole.order = index + 1;
        return userRole;
      });
    }
    userRoles?.forEach(ur => {
      if (ur.user == null && ur.userID) ur.user = this.getUser(ur.userID);
    });
    return userRoles;
  }

  navigateToCatalog(id: number) {
    const route = '/users';
    const dummyCatalog = { id: id } as PendingCatalog;
    this._catalogService.pendingCatalog = dummyCatalog;
    this.router.navigate([route]);
  }

  isUserModified(e: any) {
    if (e?.pendingDelete) return true;
    if (!this.showRoleModified) return false;
    return !this.role?.userRole?.find(
      (ur) => ur.userID === e.userID && ur.order === e.order,
    );
  }

  /**
   * Indicates whether a pending note is new or differs from its approved version.
   * @param userRole Role assignment containing the pending note.
   * @param note Pending note to compare.
   */
  isNoteModified(userRole: UserRole, note: UserRoleNote): boolean {
    if (!this.showRoleModified) return false;

    // Compare against the approved role so pending note edits are visible immediately.
    const approvedNote = this.role?.userRole
      ?.find((approvedUserRole) => approvedUserRole.userID === userRole.userID)
      ?.notes?.find((approvedUserRoleNote) => approvedUserRoleNote.id === note.id);

    return !approvedNote || approvedNote.description !== note.description;
  }

  private markDirty(): void {
    this.syncDisplayedRoleUsers();
    this.emitUsers();
    this.dirty.emit();
  }

  private emitUsers(): void {
    this.usersChange.emit(this.dataSourceRoleUsers.data ?? []);
  }

  private syncDisplayedRoleUsers(): void {
    if (this.displayedRole) {
      this.displayedRole.userRole = this.dataSourceRoleUsers.data ?? [];
    }
  }

  isPendingDelete(userRole: UserRole): boolean {
    if (this.showRoleModified && userRole?.pendingDelete !== undefined) {
      return userRole.pendingDelete === true;
    }

    if (userRole?.pendingDelete === true) return true;
    if (!userRole?.userID) return false;

    const existsInCurrentRole = this.role?.userRole?.some((roleUser) =>
      this.hasSameUserRole(roleUser, userRole),
    );
    const existsInPendingRole = this.getPendingRoleUsers()?.some(
      (roleUser) => this.hasSameUserRole(roleUser, userRole),
    );

    return !!(existsInCurrentRole && existsInPendingRole === false);
  }

  hasVisibleMenuActions(userRole: UserRole): boolean {
    if (this.isPendingDelete(userRole)) {
      return this.showRoleModified;
    }

    return true;
  }

  /**
   * Builds the role-user list for either the last approved role or the pending modification comparison.
   * Pending-only users are excluded until Show Modification is enabled.
   * @param roleUsers Users assigned to the last approved role.
   * @param displayedUsers Users currently loaded in the role details view.
   * @param pendingUsers Users stored in the pending role request.
   */
  private getUsersForDisplay(
    roleUsers: UserRole[],
    displayedUsers: UserRole[],
    pendingUsers?: UserRole[],
  ): UserRole[] {
    // Keep the standard editing flow unchanged when there is no pending role request.
    if (!this.showRoleModified && pendingUsers !== undefined) {
      return this.getUserRolesOrderedWithOrderField(
        roleUsers.map((userRole) => ({ ...userRole })),
      );
    }

    const comparisonUsers = displayedUsers;
    const sourceUsers = this.showRoleModified ? roleUsers : displayedUsers;
    const mergedUsers = sourceUsers.map((userRole) => {
      const pendingUserRole = comparisonUsers.find((comparisonUserRole) =>
        this.hasSameUserRole(comparisonUserRole, userRole),
      );

      return {
        ...userRole,
        // Display pending fields, including notes, while retaining approved-only users for deletion comparison.
        ...(this.showRoleModified && pendingUserRole ? pendingUserRole : {}),
        pendingDelete: !pendingUserRole,
      };
    });

    comparisonUsers
      .filter((userRole) => !this.hasMatchingUserRole(mergedUsers, userRole))
      .forEach((userRole) =>
        mergedUsers.push({ ...userRole, pendingDelete: false }),
      );

    return this.getUserRolesOrderedWithOrderField(mergedUsers);
  }

  private getPendingRoleUsers(): UserRole[] | undefined {
    const pendingRole = this.role?.pendingRoles?.at(-1);
    if (!pendingRole?.roleData) return undefined;

    const pendingRoleData = this.utils.JSONparse(pendingRole.roleData) as Role;
    return pendingRoleData?.userRole as UserRole[] | undefined;
  }

  private isExistingRoleUser(userRole: UserRole): boolean {
    return this.role?.userRole?.some((roleUser) =>
      this.hasSameUserRole(roleUser, userRole),
    ) ?? false;
  }

  private hasMatchingUserRole(users: UserRole[], userRole: UserRole): boolean {
    return users.some((currentUserRole) =>
      this.hasSameUserRole(currentUserRole, userRole),
    );
  }

  private hasSameUserRole(first: UserRole, second: UserRole): boolean {
    return !!(
      first?.userID &&
      second?.userID &&
      first.userID === second.userID
    );
  }
}
